import React, { useEffect, useState, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { clearAuth, hasToken } from '../services/authService';
import { apiFetch, getUserFacingErrorMessage } from '../services/apiClient';
import './Header.css';

/** Hide Evaluations nav tab (routes still exist if opened by URL). */
const SHOW_EVALUATIONS_TAB = false;

const Header = () => {
    const [user, setUser] = useState(null);
    const [showTabs, setShowTabs] = useState(false);
    const [showUserMenu, setShowUserMenu] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const userMenuRef = useRef(null);

    useEffect(() => {
        fetchUserInfo();
        const protectedRoutes = ['/jobs', '/interviews', '/upload', '/schedule-interview', '/evals', '/monitoring', '/platform', '/pending-membership'];
        if (protectedRoutes.some(route => location.pathname.startsWith(route))) {
            setShowTabs(true);
        }
    }, [location.pathname]); // Refresh when route changes

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
                setShowUserMenu(false);
            }
        };

        if (showUserMenu) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showUserMenu]);

    const fetchUserInfo = async () => {
        // Skip API call if no token stored
        if (!hasToken()) {
            // Still show tabs based on route
            const protectedRoutes = ['/jobs', '/interviews', '/upload', '/evals', '/monitoring', '/platform', '/pending-membership'];
            if (protectedRoutes.some(route => location.pathname.startsWith(route))) {
                setShowTabs(true);
            }
            return;
        }

        try {
            const response = await apiFetch('/api/auth/status');
            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                // If 404, endpoint might not exist, but still show tabs if on protected route
                if (response.status === 404) {
                    console.warn('Auth status endpoint not found, using route-based detection');
                    return;
                }
                throw new Error(
                    getUserFacingErrorMessage(response.status, data, `HTTP ${response.status}`)
                );
            }
            if (data.authenticated) {
                setUser(data.user);
                setShowTabs(true);
            } else {
                setUser(null);
                setShowTabs(false);
            }
        } catch (error) {
            console.error('Failed to fetch user info:', error);
            // Don't hide tabs on error - let route-based detection handle it
            if (error.message.includes('404') || error.message.includes('not found')) {
                // Endpoint doesn't exist, use route-based detection
                const protectedRoutes = ['/jobs', '/interviews', '/upload', '/evals', '/monitoring', '/platform', '/pending-membership'];
                setShowTabs(protectedRoutes.some(route => location.pathname.startsWith(route)));
            } else {
                // On other errors, still show tabs if we're on a protected route
                const protectedRoutes = ['/jobs', '/interviews', '/upload', '/evals', '/monitoring', '/platform', '/pending-membership'];
                if (protectedRoutes.some(route => location.pathname.startsWith(route))) {
                    setShowTabs(true);
                }
            }
        }
    };

    const toggleUserMenu = () => {
        setShowUserMenu(!showUserMenu);
    };

    const handleLogout = async () => {
        try {
            // Call logout endpoint (no-op on server for JWT, but kept for compatibility)
            await apiFetch('/api/auth/logout', { method: 'POST' });
        } catch (error) {
            console.error('Logout API call failed:', error);
        } finally {
            // Clear tokens and user state regardless of API response
            clearAuth();
            setUser(null);
            setShowUserMenu(false);
            navigate('/login');
        }
    };

    const isTenantGuest =
        user && String(user.role || '') === 'guest' && user.company_id != null;

    return (
        <header className="header">
            <div className="header-content">
                <NavLink to="/" className="header-logo">
                    <div className="logo-video-wrapper">
                        <video 
                            autoPlay 
                            loop 
                            muted 
                            playsInline 
                            className="logo-animation"
                        >
                            <source src="/orbit-logo.mp4" type="video/mp4" />
                        </video>
                    </div>
                    <h2>TalentVibe</h2>
                </NavLink>
                <nav className="header-nav">
                    <NavLink 
                        to="/" 
                        className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                    >
                        Home
                    </NavLink>
                    {(user || showTabs) && (
                        <>
                            <NavLink
                                to="/upload"
                                className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                            >
                                Upload
                            </NavLink>
                            <NavLink
                                to="/jobs"
                                className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                            >
                                Jobs
                            </NavLink>
                            {!isTenantGuest && (
                                <>
                            <NavLink
                                to="/interviews"
                                className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                            >
                                Interviews
                            </NavLink>
                            {SHOW_EVALUATIONS_TAB && (
                            <NavLink
                                to="/evals"
                                className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                            >
                                Evaluations
                            </NavLink>
                            )}
                            <NavLink
                                to="/monitoring"
                                className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                            >
                                Monitoring
                            </NavLink>
                                </>
                            )}
                            {user?.role === 'talentvibe_admin' && !user?.company_id && (
                                <NavLink
                                    to="/platform"
                                    className={({ isActive }) => (isActive ? 'nav-link active' : 'nav-link')}
                                >
                                    Platform
                                </NavLink>
                            )}
                            {(user?.role === 'company_admin' || user?.role === 'super_admin') && (
                                <NavLink 
                                    to="/settings?tab=llm" 
                                    className={({ isActive }) => (isActive ? 'nav-link active nav-link-icon' : 'nav-link nav-link-icon')}
                                    title="Settings"
                                >
                                    ⚙️
                                </NavLink>
                            )}
                        </>
                    )}
                </nav>
                <div className="header-user">
                    {user ? (
                        <div className="user-menu" ref={userMenuRef}>
                            <button 
                                className="user-menu-trigger"
                                onClick={toggleUserMenu}
                                aria-label="User menu"
                            >
                                {user.picture ? (
                                    <img 
                                        src={user.picture} 
                                        alt={user.username || user.email} 
                                        className="user-avatar" 
                                    />
                                ) : (
                                    <div className="user-icon">
                                        {user.name ? user.name.charAt(0).toUpperCase() : (user.username ? user.username.charAt(0).toUpperCase() : '👤')}
                                    </div>
                                )}
                                <span className="user-name-text">
                                    {user.name || user.username || user.email?.split('@')[0]}
                                </span>
                                <span className="dropdown-arrow">▼</span>
                            </button>
                            
                            {showUserMenu && (
                                <div className="user-dropdown">
                                    <div className="user-dropdown-header">
                                        <div className="user-dropdown-name">{user.name || user.username}</div>
                                        <div className="user-dropdown-email">{user.email}</div>
                                    </div>
                                    
                                    <div className="user-dropdown-divider"></div>
                                    
                                    {user.company && (
                                        <div className="user-dropdown-item">
                                            <span className="dropdown-label">Company:</span>
                                            <span className="dropdown-value">{user.company.name}</span>
                                        </div>
                                    )}
                                    
                                    <div className="user-dropdown-item">
                                        <span className="dropdown-label">Role:</span>
                                        <span className="dropdown-value">{user.role || 'guest'}</span>
                                    </div>
                                    
                                    {user.company?.domain && (
                                        <div className="user-dropdown-item">
                                            <span className="dropdown-label">Domain:</span>
                                            <span className="dropdown-value">{user.company.domain}</span>
                                        </div>
                                    )}
                                    
                                    <div className="user-dropdown-divider"></div>
                                    
                                    <button 
                                        onClick={handleLogout} 
                                        className="user-dropdown-logout"
                                    >
                                        Logout
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <NavLink to="/login" className="login-link">Login</NavLink>
                    )}
                </div>
            </div>
        </header>
    );
};

export default Header;
